// Result-Card — sichtbar in Admin- und Voter-View, sobald die Survey
// finalisiert ist. Zeigt die fixierten Slots, optionale Admin-Nachricht,
// und Aktionen (iCal-Download, Web-Share). Plus „Hurra"-Konfetti beim
// ersten Aufruf nach Finalisierung (per sessionStorage gemerkt, damit's
// nicht bei jedem Reload knallt).

import type { Appointment } from '../types';
import { formatDateShort, formatTime } from '../time';
import { buildResultShareMessage } from '../share-message';
import { el } from './dom';

export type ResultCardOptions = {
  surveyId: string;
  title: string | null;
  voterToken: string;
  finalSlots: Appointment[];
  finalMessage: string | null;
  /** Optionaler Survey-Ort/Link — gilt für alle finalen Slots. */
  location?: string | null;
  /** Wer schaut grade — beeinflusst Texte/Knöpfe. */
  audience: 'admin' | 'voter';
  /** Optional: erlaubt dem Admin, die Finalisierung aufzuheben. */
  onUndo?: () => void;
};

export function renderResultCard(opts: ResultCardOptions): HTMLElement {
  const card = el('section', { class: 'result-card' });
  card.append(
    el('div', { class: 'result-card-burst' }, '🎉'),
    el('div', { class: 'result-card-eyebrow' }, 'Termin steht'),
  );

  const title = opts.title?.trim() || 'Slot Umfrage';
  card.append(el('h2', { class: 'result-card-title' }, title));

  if (opts.location?.trim()) {
    card.append(renderResultLocation(opts.location.trim()));
  }

  const list = el('ul', { class: 'result-card-list' });
  for (const slot of opts.finalSlots) {
    const item = el('li', { class: 'result-card-item' });
    const when = el('span', { class: 'result-card-when' });
    when.append(
      el('span', { class: 'result-card-day' }, formatDateShort(slot.date)),
      el('span', { class: 'result-card-time' },
        `${formatTime(slot.startMinutes)}–${formatTime(slot.endMinutes)}`),
    );
    item.append(
      el('span', { class: 'result-card-check' }, '✓'),
      when,
    );
    list.append(item);
  }
  card.append(list);

  if (opts.finalMessage) {
    card.append(
      el('blockquote', { class: 'result-card-message' },
        el('span', { class: 'result-card-quote-mark' }, '"'),
        opts.finalMessage,
      ),
    );
  }

  // Aktionen — Calendar-Download + Web-Share + WhatsApp/Mail-Direkt
  const voterUrl = `${window.location.origin}/vote/${opts.voterToken}`;
  const share = buildResultShareMessage({
    title: opts.title,
    finalSlots: opts.finalSlots,
    finalMessage: opts.finalMessage,
    location: opts.location ?? null,
    voterUrl,
  });

  const actions = el('div', { class: 'result-card-actions' });

  const icalBtn = el('button', { type: 'button', class: 'btn-primary result-card-action' },
    '📅 In den Kalender');
  icalBtn.addEventListener('click', () => downloadResultIcs(opts));
  actions.append(icalBtn);

  if (typeof navigator.share === 'function') {
    const shareBtn = el('button', { type: 'button', class: 'btn-secondary result-card-action' },
      '↗ Teilen');
    shareBtn.addEventListener('click', async () => {
      try {
        await navigator.share({ title: share.title, text: share.text, url: share.url });
      } catch { /* User-Cancel */ }
    });
    actions.append(shareBtn);
  }
  actions.append(
    el('a', {
      class: 'btn-secondary result-card-action',
      href: share.whatsapp, target: '_blank', rel: 'noopener noreferrer',
    }, '🟢 WhatsApp'),
    el('a', {
      class: 'btn-secondary result-card-action',
      href: share.mailto,
    }, '✉️ Mail'),
  );

  if (opts.audience === 'admin' && opts.onUndo) {
    const undoBtn = el('button',
      { type: 'button', class: 'btn-secondary result-card-action result-card-undo',
        title: 'Finalisierung aufheben — Stimmen bleiben erhalten' },
      '↺ Auswahl aufheben',
    );
    undoBtn.addEventListener('click', () => {
      const ok = window.confirm('Finalisierung aufheben? Die Umfrage wird wieder geöffnet, alle Stimmen bleiben.');
      if (ok && opts.onUndo) opts.onUndo();
    });
    actions.append(undoBtn);
  }

  card.append(actions);

  // Hurra-Konfetti — nur einmal pro Session pro Survey, damit's beim
  // Refresh nicht nervt.
  const seenKey = `slot:result-seen:${opts.surveyId}`;
  if (!sessionStorage.getItem(seenKey)) {
    sessionStorage.setItem(seenKey, '1');
    queueMicrotask(() => triggerHurra(card));
  }

  return card;
}

/** Konfetti-Burst über den ganzen Viewport. Reduced-Motion respektieren. */
function triggerHurra(_card: HTMLElement): void {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const overlay = document.createElement('div');
  overlay.className = 'hurra-overlay';
  document.body.append(overlay);
  const colors = ['#2f9e56', '#d4a23c', '#c4574a', '#7c3aed', '#1e40af'];
  const N = 80;
  for (let i = 0; i < N; i++) {
    const piece = document.createElement('span');
    piece.className = 'hurra-piece';
    piece.style.background = colors[Math.floor(Math.random() * colors.length)];
    piece.style.left = `${Math.random() * 100}%`;
    piece.style.animationDelay = `${Math.random() * 0.4}s`;
    piece.style.animationDuration = `${2.2 + Math.random() * 1.5}s`;
    piece.style.transform = `rotate(${Math.random() * 360}deg)`;
    overlay.append(piece);
  }
  setTimeout(() => overlay.remove(), 4500);
}

/** iCal-Datei der finalen Slots — STATUS:CONFIRMED. */
function downloadResultIcs(opts: ResultCardOptions): void {
  const titlePrefix = opts.title?.trim() || 'Slot';
  const stamp = formatStamp(new Date());
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//slot.gotyou.at//Slot Final Result//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];
  for (const slot of opts.finalSlots) {
    const event: string[] = [
      'BEGIN:VEVENT',
      `UID:slot-final-${opts.surveyId}-${slot.id}@slot.gotyou.at`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${formatLocal(slot.date, slot.startMinutes)}`,
      `DTEND:${formatLocal(slot.date, slot.endMinutes)}`,
      `SUMMARY:${escapeIcs(`✅ ${titlePrefix}`)}`,
      'STATUS:CONFIRMED',
      'CATEGORIES:Slot',
    ];
    if (opts.location?.trim()) event.push(`LOCATION:${escapeIcs(opts.location.trim())}`);
    event.push(
      opts.finalMessage ? `DESCRIPTION:${escapeIcs(opts.finalMessage)}` : 'DESCRIPTION:Festgelegter Termin aus Slot.',
      'END:VEVENT',
    );
    lines.push(...event);
  }
  lines.push('END:VCALENDAR');
  const text = lines.join('\r\n') + '\r\n';
  const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (opts.title?.trim() || 'slot')
    .replace(/[^A-Za-z0-9äöüÄÖÜß \-_]/g, '')
    .substring(0, 40)
    .trim() || 'slot';
  a.href = url;
  a.download = `slot-result-${safeName}.ics`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Survey-Location-Block — als Link, wenn http(s)://; sonst Klartext. */
function renderResultLocation(location: string): HTMLElement {
  const isUrl = /^https?:\/\//i.test(location);
  const wrap = el('p', { class: 'result-card-location' }, '📍 ');
  if (isUrl) {
    wrap.append(el('a', {
      href: location,
      target: '_blank',
      rel: 'noopener noreferrer',
      class: 'result-card-location-link',
    }, location));
  } else {
    wrap.append(location);
  }
  return wrap;
}

function pad2(n: number): string { return String(n).padStart(2, '0'); }
function formatStamp(d: Date): string {
  return (
    String(d.getUTCFullYear()) + pad2(d.getUTCMonth() + 1) + pad2(d.getUTCDate()) +
    'T' + pad2(d.getUTCHours()) + pad2(d.getUTCMinutes()) + pad2(d.getUTCSeconds()) + 'Z'
  );
}
function formatLocal(date: string, minutes: number): string {
  return date.replace(/-/g, '') + 'T' + pad2(Math.floor(minutes / 60)) + pad2(minutes % 60) + '00';
}
function escapeIcs(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}
